import React from "react";
import {observer} from "mobx-react-lite";
import NumberBox from "../../atoms/input/numberBox";
import "./eventBookingSpaceEdit.sass";
import {useTranslation} from "react-i18next";
import {EventBookingSpaceDetails} from "../../../stores/domainStore/pages/models/eventBookingSpaceDetails";
import parse from "html-react-parser";

export interface IEventBookingSpaceEditProps {
  eventBookingSpace: EventBookingSpaceDetails;
  isEditingEventSeries?: boolean;
  canNonMakeCosmeticChanges?: boolean;
}

const EventBookingSpaceEdit: React.FC<IEventBookingSpaceEditProps> = ({
  eventBookingSpace,
  isEditingEventSeries,
  canNonMakeCosmeticChanges
}) => {
  const {t} = useTranslation(["event"]);
  const getRemainingSpace = () => {
    const totalSpace = eventBookingSpace.totalSpace;
    const reservedSpace = eventBookingSpace.reservedSpace;

    if (!totalSpace.number && !reservedSpace.number) {
      return <span>{t("event:labels.space.autoCalculates")}</span>;
    } else if (
      !totalSpace.isValid ||
      !reservedSpace.isValid ||
      (totalSpace.number ?? 0) < (reservedSpace.number ?? 0)
    ) {
      return t("event:errors.space.error");
    }

    return ((totalSpace.number ?? 0) - (reservedSpace.number ?? 0)).toString();
  };

  const getReservedSpaceErrorText = () => {
    if (eventBookingSpace.isStaffSpaceMoreThanCapacity())
      return t("event:errors.space.staffSpaceMoreThanCapacity");
    else if (eventBookingSpace.isStaffBookingMorethanRemainingSpace()) {
      return t("event:errors.space.staffBookingMorethanRemainingSpace");
    } else if (!eventBookingSpace.reservedSpace.isValid)
      return t("event:errors.space.reservedSpaceEmpty", {
        min: eventBookingSpace.reservedSpace.min!.toString(),
        max: eventBookingSpace.reservedSpace.max!.toString()
      });
    return "";
  };

  const getTotalSpaceErrorText = () => {
    if (eventBookingSpace.isCapacityLessThanConfirmBooking())
      return t("event:errors.space.totalBooking", {
        totalBooking: eventBookingSpace.totalBookings
      });
    else if (!eventBookingSpace.totalSpace.isValid)
      return t("event:errors.space.totalSpaceEmpty", {
        min: eventBookingSpace.totalSpace.min!.toString(),
        max: eventBookingSpace.totalSpace.max!.toString()
      });
    return "";
  };

  const getTotalSpaceWarningText = () => {
    if (
      isEditingEventSeries &&
      !eventBookingSpace.isUnlimitedCapacity &&
      eventBookingSpace.totalSpaceBelowHighestBookingCountForSeries
    ) {
      return canNonMakeCosmeticChanges
        ? t("event:errors.bookabilityWarningMessage")
        : parse(
            t("event:errors.totalSpaceLessThanConfirmedBooking", {
              totalBookingCount: eventBookingSpace.highestBookingCountForSeries
            })
          ).toString();
    }
    return "";
  };

  return (
    <>
      <div className="event-booking-space-edit">
        <div className="total-space">
          <div>{t("event:labels.space.totalSpace")}</div>
          <div>{t("event:labels.space.totalSpaceHelp")}</div>
          <NumberBox
            value={eventBookingSpace.totalSpace}
            placeholder={t("event:placeholders.noLimit")}
            errorText={
              !eventBookingSpace.totalSpace.isValid
                ? getTotalSpaceErrorText()
                : getTotalSpaceWarningText()
            }
          />
        </div>
        <span>-</span>
        <div className="reserved-space">
          <div>{t("event:labels.space.reservedSpace")}</div>
          <div>{t("event:labels.space.reservedSpaceHelp")}</div>
          <NumberBox
            value={eventBookingSpace.reservedSpace}
            errorText={
              !eventBookingSpace.reservedSpace.isValid
                ? getReservedSpaceErrorText()
                : ""
            }
            placeholder={
              eventBookingSpace.totalSpace.number
                ? t("event:placeholders.reservedSpace")
                : t("event:placeholders.noLimit")
            }
          />
        </div>
        <span>=</span>
        <div className="remaining-space">
          <div>{t("event:labels.space.remainingSpace")}</div>
          <div>{t("event:labels.space.remainingSpaceHelp")}</div>
          <div data-testid="remainingSpace">{getRemainingSpace()}</div>
        </div>
      </div>
    </>
  );
};
export default observer(EventBookingSpaceEdit);
